import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import {
    BankIcon,
    LuckySpinIcon,
    LogoutIcon,
    PasswordIcon,
    PlatformIcon,
    RulesIcon,
    WithdrawalHistoryIcon,
} from '../Components/icons';
import Rules from '../Components/Rules';
import About from '../Components/About';
import { removeAuthToken } from '../utils/storageUtil';
import BankInfo from '../Components/BankInfo';
import axiosInstance from '../utils/axiosInstance';
import { formatNumber } from '../utils/numberUtil';
import { runSafely } from '../utils/runSafely';
import { useTranslation } from 'react-i18next';
import SpinLoader from '../Components/SpinLoader';
import AppShell from '../Components/AppShell';
import Card from '../Components/Card';
import Container from '../Components/Container';
import WalletPanel from './WalletPanel';


interface UserInfoType {
    id: number;
    username: string;
    walletBalances: number;
    creditScore: number;
    orders: number;
    completed: number;
    commissions: number;
    bonus: number;
    vipLevel: number;

    name: string;
    bank: string;
    account: string;
    phoneNo: string;
    notificationDate: Date;
}

/** Everything a service tile needs to act, passed in so the list stays declarative. */
interface ServiceActions {
    navigate: (to: string) => void;
    setShowBank: (open: boolean) => void;
    setShowRules: (open: boolean) => void;
    setShowAbout: (open: boolean) => void;
}

/** Shared by the identity card's micro-labels, the stat cards and the section headers. */
const MICRO_LABEL = 'text-[11px] uppercase tracking-[0.16em] text-brand-600';

/**
 * The six account services, in display order.
 *
 * Declared as data rather than hand-written markup so the grid reflows and
 * adding a service is a one-line edit. Every target and every handler here
 * is the same as before this list existed — nothing was added or removed.
 */
const SERVICES: {
    key: string;
    labelKey: string;
    Icon: React.FC<{ 'aria-hidden'?: boolean | 'true' | 'false' }>;
    onSelect: (a: ServiceActions) => void;
}[] = [
        { key: 'bank', labelKey: 'bank', Icon: BankIcon, onSelect: (a) => a.setShowBank(true) },
        // Labelled `withdrawal_history`, not `accountInfo`: this tile opens the
        // withdrawal history page, and the old label said "Account Info".
        { key: 'history', labelKey: 'withdrawal_history', Icon: WithdrawalHistoryIcon, onSelect: (a) => a.navigate('/withdrawal-history') },
        { key: 'rules', labelKey: 'rules', Icon: RulesIcon, onSelect: (a) => a.setShowRules(true) },
        { key: 'platform', labelKey: 'platform', Icon: PlatformIcon, onSelect: (a) => a.setShowAbout(true) },
        { key: 'password', labelKey: 'password', Icon: PasswordIcon, onSelect: (a) => a.navigate('/password') },
        // Lucky Spin is deliberately absent: it gets its own full-width row above
        // this grid. As a seventh tile it left one orphan on a third row, and a
        // promotion reads oddly sitting between Password and Logout anyway.
        {
            key: 'logout', labelKey: 'logout', Icon: LogoutIcon, onSelect: (a) => {
                removeAuthToken();
                a.navigate('/login');
            }
        },
    ];

const AccountPage: React.FC = () => {
    const [userInfo, setUserInfo] = useState<UserInfoType>();
    const navigate = useNavigate();

    const [showRules, setShowRules] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [showBank, setShowBank] = useState(false);

    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const getUserInfo = async () => {
        setLoading(true);
        const res = await axiosInstance.get("/protected/getUserInfo")
        const data = res.data.data;
        if (data) {
            const user = data.user;
            if (!user.isActive) {
                navigate("/login");
                return;
            }

            const userOrders = data.userOrders;
            const userBank = data.userBank;

            setUserInfo({
                id: user.id,
                username: user.username,
                walletBalances: user.walletBalances,
                commissions: user.totalCommission,
                creditScore: user.creditScore,
                bonus: user.bonus,
                vipLevel: user.vipLevel,
                notificationDate: user.notificationDate,

                orders: userOrders?.length ?? 0,
                completed: userOrders?.filter((e: any) => e.orderStatus === "completed")?.length ?? 0,

                account: userBank?.account,
                bank: userBank?.bank,
                name: userBank?.name,
                phoneNo: userBank?.phoneNo,
            })

            if (user.notificationDate && new Date(user.notificationDate) > new Date()) {
                Swal.fire({
                    title: 'Warning!',
                    text: 'KUOTA PAKEJ YANG ANDA PILIH SEMUA SUDAH HABIS. SILA PILIH SEMULA PAKEJ ANDA DAN MEMBUAT TAMBAH NILAI DALAM MASA 24 JAM, JIKA TIDAK JUMLAH YANG ANDA TAMBAH NILAI AKAN DILUPUSKAN.',
                    icon: 'warning',
                })
            }
        } else {
            navigate("/login")
        }
        setTimeout(() => {
            setLoading(false);
        }, 1500);

    }

    // Mount-only fetch. `getUserInfo` is redefined every render, so adding it
    // to the dependency array would refetch in a loop: fetch -> setState ->
    // rerender -> new identity -> fetch.
    useEffect(() => {
        runSafely('AccountPage.getUserInfo', getUserInfo, () => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * The four account stats, in display order.
     *
     * Data rather than four hand-written cards, for the same reason `SERVICES`
     * is: the grid reflows and the card treatment is written once. Every value
     * is set in Playfair, counts included — mixing serif money figures with
     * sans-serif counts in one row of four made the row look unfinished.
     */
    const STATS: { key: string; labelKey: string; value: React.ReactNode }[] = [
        { key: 'order', labelKey: 'order', value: userInfo?.orders ?? 0 },
        { key: 'completed', labelKey: 'completed', value: userInfo?.completed ?? 0 },
        { key: 'commission', labelKey: 'commission', value: `RM ${formatNumber(userInfo?.commissions ?? 0)}` },
        { key: 'bonus', labelKey: 'bonus_balance', value: `RM ${formatNumber(userInfo?.bonus ?? 0)}` },
    ];

    return (
        <AppShell backdrop="/images/my-bg.png">
            {loading && <SpinLoader />}
            <Container>
                <div className='p-5 flex flex-col gap-4 lg:grid lg:grid-cols-[380px_1fr] lg:gap-10 lg:items-start'>
                    {/* Left column: identity + wallet. `contents` below lg dissolves this
                        wrapper's own box so its two children fall back to being direct
                        flex items of the div above, spaced by that div's existing
                        (unprefixed) gap-4. At lg it becomes a real flex column and a
                        single sticky unit, so it stays in view while the taller right
                        column scrolls. */}
                    <div className='contents lg:flex lg:flex-col lg:gap-4 lg:sticky lg:top-28'>
                        <Card className='lg:p-6'>
                            <div className='flex items-center gap-4'>
                                {/* The ring is a sibling overlay rather than a border on the
                                    <img>: a border would shrink the rendered image inside its
                                    own box, leaving the avatar a hair smaller than 56px. */}
                                <span className='relative shrink-0'>
                                    <img
                                        src='/images/avatar.png'
                                        width={56}
                                        height={56}
                                        alt=''
                                        className='block h-14 w-14 rounded-full object-cover'
                                    />
                                    <span
                                        aria-hidden='true'
                                        className='absolute inset-0 rounded-full ring-1 ring-inset ring-accent-500/45'
                                    />
                                </span>
                                <div className='min-w-0'>
                                    <div className={MICRO_LABEL}>{t("username")}</div>
                                    <div className='truncate font-display text-xl text-brand-900'>
                                        {userInfo?.username ?? '—'}
                                    </div>
                                </div>
                            </div>

                            <div className='mt-5 grid grid-cols-2 gap-4 border-0 border-t border-solid border-brand-300/70 pt-4'>
                                <div>
                                    <div className={MICRO_LABEL}>{t("id")}</div>
                                    <div className='mt-1 text-sm tabular-nums text-brand-900'>
                                        {userInfo?.id ? (88800000 + userInfo?.id) : "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <div className={MICRO_LABEL}>{t("credit_score")}</div>
                                    <div className='mt-1 text-sm tabular-nums text-brand-900'>
                                        {userInfo?.creditScore ?? 0}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <WalletPanel
                            balance={userInfo?.walletBalances ?? 0}
                            vipLevel={userInfo?.vipLevel ?? 0}
                            creditScore={userInfo?.creditScore ?? 0}
                            onWithdraw={() => navigate("/withdrawal")}
                            // Only true before the first response lands. A refetch
                            // (after saving bank details) already has a real balance
                            // on screen, and shimmering over it would read as a fault.
                            pending={!userInfo}
                        />
                    </div>

                    {/* Right column: stat cards, then menu tiles. Same `contents` treatment —
                        below lg this wrapper contributes nothing and its two children fall back
                        to being direct flex items of the div above. */}
                    <div className='contents lg:flex lg:flex-col lg:gap-8'>
                        {/* No section header. The VIP level it used to carry now sits in the
                            wallet panel's chip, and at lg this grid's top edge lines up with
                            the identity card's, so the two columns start on the same line.

                            2×2 at every width, including xl. Four across left each card ~125px
                            of inner width, which is narrower than "RM 3240.00" set in 34px
                            Playfair — every money card wrapped onto two lines and dragged the
                            whole row taller to match. */}
                        <div className='grid grid-cols-2 gap-3 lg:gap-4'>
                            {STATS.map(({ key, labelKey, value }) => (
                                <div
                                    key={key}
                                    className={
                                        'group relative overflow-hidden rounded-md border border-solid ' +
                                        'border-brand-300 bg-white p-4 shadow-card lg:p-6'
                                    }
                                >
                                    <span
                                        aria-hidden='true'
                                        className={
                                            'absolute inset-x-0 top-0 h-px bg-accent-500 opacity-0 ' +
                                            'transition-opacity duration-200 group-hover:opacity-70'
                                        }
                                    />
                                    <div className={MICRO_LABEL}>{t(labelKey)}</div>
                                    <div className='mt-2 font-display text-xl tabular-nums text-brand-900 lg:text-display-md'>
                                        {value}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className='mt-6 flex flex-col gap-4 lg:mt-0'>
                            <div className='font-display text-xl text-brand-900 pb-4 border-0 border-b border-solid border-brand-300'>
                                {t("service")}
                            </div>

                            {/* Lucky Spin leads the section on its own row rather than as a
                                seventh tile in the grid below — seven across three columns
                                orphans one on a third row. Full width also gives the one
                                promotional item here the prominence a prize draw wants, and
                                it is the only place besides the wallet panel that spends the
                                gold accent. */}
                            {/* <button
                                type='button'
                                onClick={() => navigate('/lucky-spin')}
                                className={
                                    'group flex w-full items-center gap-4 rounded-md border border-solid ' +
                                    'border-accent-500/40 bg-white px-4 py-4 cursor-pointer text-left ' +
                                    'transition-all duration-200 hover:border-accent-500 hover:shadow-card ' +
                                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                                    'focus-visible:outline-accent-500'
                                }
                            >
                                <span
                                    className={
                                        'flex h-12 w-12 shrink-0 items-center justify-center rounded-full ' +
                                        'bg-accent-500/15 text-accent-600 text-[17px] ring-1 ring-inset ring-accent-500/40'
                                    }
                                >
                                    <LuckySpinIcon aria-hidden='true' />
                                </span>
                                <span className='flex-1 text-xs uppercase tracking-[0.16em] text-brand-900'>
                                    {t("lucky_spin")}
                                </span>
                                <span
                                    aria-hidden='true'
                                    className='text-brand-600 leading-none transition-transform duration-200 group-hover:translate-x-0.5'
                                >
                                    &rarr;
                                </span>
                            </button> */}
                            {/*
                              One data-driven grid rather than two hand-built rows of
                              three: adding or removing a service is a list edit, and the
                              tiles reflow instead of being pinned to a fixed row shape.
                              Each is a real <button>, so it is keyboard reachable and
                              focusable — the old clickable <div>s were neither.
                            */}
                            <div className='grid grid-cols-3 gap-3 lg:gap-4'>
                                {SERVICES.map(({ key, labelKey, Icon, onSelect }) => (
                                    <button
                                        key={key}
                                        type='button'
                                        onClick={() => onSelect({ navigate, setShowBank, setShowRules, setShowAbout })}
                                        className={
                                            'group flex flex-col items-center gap-2.5 rounded-md border border-solid ' +
                                            'border-brand-300 bg-white px-2 py-5 cursor-pointer ' +
                                            'transition-all duration-200 hover:border-accent-500/60 ' +
                                            'hover:shadow-card hover:-translate-y-0.5 active:translate-y-0 ' +
                                            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                                            'focus-visible:outline-accent-500'
                                        }
                                    >
                                        <span
                                            className={
                                                'flex h-12 w-12 items-center justify-center rounded-full ' +
                                                'bg-brand-100 text-brand-800 text-[17px] ring-1 ring-inset ring-brand-300/70 ' +
                                                // Alpha modifiers must land on Tailwind's opacity scale
                                                // (multiples of 5) or they are silently dropped at build
                                                // time. /15 also matches SupportPage's identical tiles.
                                                'transition-colors duration-200 group-hover:bg-accent-500/15 ' +
                                                'group-hover:text-accent-600 group-hover:ring-accent-500/40'
                                            }
                                        >
                                            <Icon aria-hidden='true' />
                                        </span>
                                        <span className='text-[11px] uppercase tracking-[0.08em] text-brand-900 leading-tight text-center'>
                                            {t(labelKey)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Container>

            <Rules isOpen={showRules}
                closeModal={() => setShowRules(false)} />

            <About isOpen={showAbout}
                closeModal={() => setShowAbout(false)} />

            <BankInfo isOpen={showBank}
                name={userInfo?.name ?? ""}
                account={userInfo?.account ?? ""}
                bank={userInfo?.bank ?? ""}
                phoneNo={userInfo?.phoneNo ?? ""}
                closeModal={(refresh: boolean) => {
                    setShowBank(false)
                    if (refresh) {
                        runSafely('AccountPage.getUserInfo (bank refresh)', getUserInfo, () => setLoading(false));
                    }
                }
                } />
        </AppShell>
    );
}

export default AccountPage;
